import { useState } from "react";
import { toast } from "react-toastify";

import type { WriterFormData } from "./useWriterForm";

import { useCreateWriter, useUpdateWriter } from "../../../../../api/mutations";
import useAppStore from "../../../../../store/appStateZustand";
import useWriterStore from "../../songs/album/store/writerStore";

type Writer = {
  writercode?: string;
  writername?: string;
  firstname?: string;
  lastname?: string;
  middlename?: string;
  caeipinumber?: string;
  societycisaccode?: string;
  dateofbirth?: string;
  publishername?: string | unknown;
  publishercaeipi?: string | unknown;
  etag?: string;
};

type UseWriterMutationOptions = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onValidationError?: (messages: string[]) => void;
};

export function useWriterMutation(options: UseWriterMutationOptions = {}) {
  const { onSuccess, onError, onValidationError } = options;
  const [pending, setPending] = useState(false);

  const selectedAccount = useAppStore(s => s.selectedAccount);
  const { addSelectedWriter, updateSelectedWriter } = useWriterStore();
  const createWriterMutation = useCreateWriter();
  const updateWriterMutation = useUpdateWriter();

  const createWriter = async (
    data: WriterFormData,
    isUSPro: boolean,
  ) => {
    setPending(true);

    try {
      // Handle date of birth formatting - matches legacy logic
      let formattedDateOfBirth = "";
      if (data.dateofbirth != null && data.dateofbirth.trim() !== "") {
        // For now, we expect dateofbirth to be in ISO format already
        // In legacy, it uses moment to format from year/month/day components
        formattedDateOfBirth = data.dateofbirth.trim();
      }

      // CREATE mutation - matches legacy WritersForm addWriter logic
      const createData = {
        artistaccountcode: selectedAccount,
        firstname: data.firstname.trim(),
        lastname: data.lastname.trim(),
        middlename: (data.middlename != null && data.middlename.trim() !== "") ? data.middlename.trim() : "",
        caeipinumber: (data.caeipinumber != null && data.caeipinumber.trim() !== "") ? data.caeipinumber.trim() : "",
        societycisaccode: (data.societycisaccode != null && data.societycisaccode.trim() !== "") ? data.societycisaccode.trim() : "",
        dateofbirth: formattedDateOfBirth,
        // Publisher fields: use empty string for creates when not US PRO (legacy pattern)
        publishername: isUSPro ? ((data.associatedPublisherName != null && data.associatedPublisherName.trim() !== "") ? data.associatedPublisherName.trim() : "") : "",
        publishercaeipi: isUSPro ? ((data.associatedPublisherCAEIPI != null && data.associatedPublisherCAEIPI.trim() !== "") ? data.associatedPublisherCAEIPI.trim() : "") : "",
        isunknownwriter: false,
      };

      const result = await createWriterMutation.mutateAsync(createData);

      if (result.model) {
        // Create display name from firstname/lastname if writername is not available
        let displayName = result.model.writername;
        if (!displayName || displayName.trim() === "") {
          const parts = [result.model.firstname, result.model.middlename, result.model.lastname].filter(Boolean);
          displayName = parts.join(" ");
        }

        const updatedWriter = {
          writercode: result.model.writercode,
          writername: displayName,
          caeipinumber: result.model.caeipinumber,
        };

        // Add the newly created writer to selected writers
        addSelectedWriter(updatedWriter);
        toast.success(`Writer created: ${updatedWriter.writername}`);

        onSuccess?.();
        return result;
      }
      else {
        throw new Error("Failed to create writer - no model returned");
      }
    }
    catch (error) {
      console.error("Error creating writer:", error);

      // Check if this is a validation error (412) with messages
      if (error instanceof Error && error.message && onValidationError) {
        // The error message from processRes contains the joined messages
        // Split them back into an array for consistent handling
        const messages = error.message.split(", ");
        onValidationError(messages);
      }
      else {
        // Generic error handling
        toast.error("Failed to create writer");
        onError?.(error as Error);
      }
      throw error;
    }
    finally {
      setPending(false);
    }
  };

  const updateWriter = async (
    data: WriterFormData,
    isUSPro: boolean,
    editingWriterCode: string,
    editingWriter: Writer | null | undefined,
    editingWriterFromAvailable: Writer | null,
    writerQueryData: Writer | undefined,
  ) => {
    setPending(true);

    try {
      // Handle date of birth formatting - matches legacy logic
      let formattedDateOfBirth = "";
      if (data.dateofbirth != null && data.dateofbirth.trim() !== "") {
        // For now, we expect dateofbirth to be in ISO format already
        // In legacy, it uses moment to format from year/month/day components
        formattedDateOfBirth = data.dateofbirth.trim();
      }

      // Get original date of birth for updates (if date wasn't changed)
      const originalDateOfBirth = editingWriter && "dateofbirth" in editingWriter
        ? (editingWriter.dateofbirth ?? "")
        : "";

      // UPDATE mutation - matches legacy WritersForm update logic
      // Use the most current etag - prioritize API data, then availableWriters
      let currentEtag;
      if (writerQueryData && "etag" in writerQueryData) {
        currentEtag = writerQueryData.etag;
      }
      else if (editingWriterFromAvailable && "etag" in editingWriterFromAvailable) {
        currentEtag = editingWriterFromAvailable.etag;
      }

      const updateData = {
        artistaccountcode: selectedAccount,
        writercode: editingWriterCode,
        firstname: data.firstname.trim(),
        lastname: data.lastname.trim(),
        middlename: (data.middlename != null && data.middlename.trim() !== "") ? data.middlename.trim() : "",
        caeipinumber: (data.caeipinumber != null && data.caeipinumber.trim() !== "") ? data.caeipinumber.trim() : "",
        societycisaccode: (data.societycisaccode != null && data.societycisaccode.trim() !== "") ? data.societycisaccode.trim() : "",
        // Use new formatted date if provided, otherwise keep original (legacy pattern: formattedDOB || apiDOB)
        dateofbirth: formattedDateOfBirth || originalDateOfBirth,
        // Publisher fields: use null for updates when not US PRO (legacy pattern)
        publishername: isUSPro ? ((data.associatedPublisherName != null && data.associatedPublisherName.trim() !== "") ? data.associatedPublisherName.trim() : "") : null,
        publishercaeipi: isUSPro ? ((data.associatedPublisherCAEIPI != null && data.associatedPublisherCAEIPI.trim() !== "") ? data.associatedPublisherCAEIPI.trim() : "") : null,
        // For updates, we need to include the etag for version control - use the most current one
        etag: currentEtag,
      };

      // Debug logging for etag issues
      if (currentEtag == null || currentEtag === "") {
        console.warn("No etag available for update:", {
          editingWriterCode,
          hasApiData: !!writerQueryData,
          hasAvailableWriter: !!editingWriterFromAvailable,
          apiDataEtag: writerQueryData && "etag" in writerQueryData ? writerQueryData.etag : "none",
          availableWriterEtag: editingWriterFromAvailable && "etag" in editingWriterFromAvailable ? editingWriterFromAvailable.etag : "none",
        });
      }

      const result = await updateWriterMutation.mutateAsync(updateData);

      if (result.model) {
        // Create display name from firstname/lastname if writername is not available
        let displayName = result.model.writername;
        if (!displayName || displayName.trim() === "") {
          const parts = [result.model.firstname, result.model.middlename, result.model.lastname].filter(Boolean);
          displayName = parts.join(" ");
        }

        const updatedWriter = {
          writercode: result.model.writercode,
          writername: displayName,
          caeipinumber: result.model.caeipinumber,
        };

        // Update the existing writer in the selected list
        updateSelectedWriter(editingWriterCode, updatedWriter);
        toast.success(`Writer updated: ${updatedWriter.writername}`);

        onSuccess?.();
        return result;
      }
      else {
        throw new Error("Failed to update writer - no model returned");
      }
    }
    catch (error) {
      console.error("Error updating writer:", error);

      // Check if this is a validation error (412) with messages
      if (error instanceof Error && error.message && onValidationError) {
        // The error message from processRes contains the joined messages
        // Split them back into an array for consistent handling
        const messages = error.message.split(", ");
        onValidationError(messages);
      }
      else {
        // Generic error handling
        toast.error("Failed to update writer");
        onError?.(error as Error);
      }
      throw error;
    }
    finally {
      setPending(false);
    }
  };

  return {
    createWriter,
    updateWriter,
    pending,
  };
}
